import type { CapabilityKey } from "@/hooks/useCapabilities";

import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions, type SecurePermissions } from "@webuild-ai/react-auth";

import { useCapabilities } from "@/hooks/useCapabilities";
import { logAccessAttempt } from "@/services/accessAuditLog";
import { LoadingContent } from "@/pages/Loading";

export type PermissionKey = keyof SecurePermissions;

interface ProtectedRouteProps {
  /**
   * The permission required to access this route
   */
  requiredPermission: PermissionKey;
  /**
   * An additional, finer-grained capability the route requires, checked on top
   * of `requiredPermission`. Use it where the coarse flag is too permissive:
   * `permissions.workflows` is computed from the RBAC *read* grant, so it is not
   * enough on its own to guard an authoring surface like the Workflow Builder.
   */
  requiredCapability?: CapabilityKey;
  /**
   * The component to render if authorised
   */
  children: React.ReactNode;
  /**
   * Optional redirect path when unauthorised (defaults to /access-denied)
   */
  redirectTo?: string;
}

/**
 * ProtectedRoute component that guards routes based on user permissions.
 *
 * This component:
 * - Checks if the user has the required permission, and the required capability
 *   when one is given
 * - Logs access attempts for audit purposes
 * - Redirects unauthorised users to the access denied page
 *
 * Uses the already-loaded permissions from AuthUserProvider context,
 * so there is no additional latency from API calls.
 *
 * @example
 * <ProtectedRoute requiredPermission="monitoring">
 *   <MetricsPage />
 * </ProtectedRoute>
 *
 * @example
 * <ProtectedRoute requiredCapability="canAuthorWorkflows" requiredPermission="workflows">
 *   <WorkflowBuilderPage />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredPermission,
  requiredCapability,
  children,
  redirectTo = "/access-denied",
}) => {
  const { permissions, isLoading } = usePermissions();
  const capabilities = useCapabilities();
  const location = useLocation();

  // A capability is RBAC-backed, and RBAC grants land a commit after the first
  // render, so a decision taken before they settle would deny an entitled user
  // and redirect them away from a route they can in fact reach.
  const isAwaitingCapabilities = requiredCapability !== undefined && !capabilities.isSettled;

  const hasRequiredPermission = permissions[requiredPermission] || false;
  const hasRequiredCapability =
    requiredCapability === undefined || Boolean(capabilities[requiredCapability]);

  const hasPermission = hasRequiredPermission && hasRequiredCapability;

  // Log the access attempt only when route, permission, or access status changes
  useEffect(() => {
    if (!isLoading && !isAwaitingCapabilities) {
      logAccessAttempt({
        route: location.pathname,
        permission: requiredPermission,
        capability: requiredCapability,
        granted: hasPermission,
        // A user turned away by the capability check may well hold the coarse
        // permission, so record which gate closed rather than leaving the audit
        // trail to imply the permission was missing.
        deniedBy: hasPermission ? undefined : hasRequiredPermission ? "capability" : "permission",
      });
    }
  }, [
    location.pathname,
    requiredPermission,
    requiredCapability,
    hasPermission,
    hasRequiredPermission,
    isLoading,
    isAwaitingCapabilities,
  ]);

  // While permissions are loading, render nothing (PrivateRoute handles loading state)
  if (isLoading) {
    return null;
  }

  // Capabilities resolve a commit later than permissions do, so hold the route
  // rather than deciding on an empty grant list.
  if (isAwaitingCapabilities) {
    return <LoadingContent />;
  }

  if (!hasPermission) {
    // Redirect to safe page with state indicating access was denied
    return (
      <Navigate
        replace
        state={{
          accessDenied: true,
          attemptedRoute: location.pathname,
          requiredPermission,
          requiredCapability,
        }}
        to={redirectTo}
      />
    );
  }

  return <>{children}</>;
};

/**
 * Route permission configuration for easy reference and maintenance.
 * Maps route paths to their required permissions.
 *
 * "/workflows" covers the child routes only (playground, edit, datasets,
 * metrics, runs). The "/workflows" index itself redirects to Library and is
 * not gated, so it never reaches ProtectedRoute. "/prompts" is likewise a bare
 * redirect since the Prompt Library was folded into Library (ADR-0019), so it
 * carries no permission entry.
 *
 * "/workflow-builder" additionally requires the `canAuthorWorkflows` capability,
 * which this map cannot express — the flag alone would admit a read-only role to
 * an editor that saves. The route declares it directly in App.tsx.
 */
export const routePermissions: Record<string, PermissionKey> = {
  "/workflows": "workflows",
  "/workflow-builder": "workflows",
  "/datahub": "knowledge_hub",
  "/metrics": "monitoring",
};

/**
 * Helper to get the required permission for a given route
 */
export const getRoutePermission = (path: string): PermissionKey | null => {
  // Check exact match first
  if (routePermissions[path]) {
    return routePermissions[path];
  }

  // Check if path starts with any protected route
  for (const [route, permission] of Object.entries(routePermissions)) {
    if (path.startsWith(route)) {
      return permission;
    }
  }

  return null;
};
